import { HelpCircle, Info } from 'lucide-react';

const CARDS = [
  { key: 'vat_percent', label: 'IVA', suffix: '%', hint: 'Taxa geral em Portugal em 2026. Aplicada sobre o preço bruto para calcular o valor líquido; todas as métricas de rentabilidade neste plano estão em euros líquidos de IVA.' },
  { key: 'tax_rate_percent', label: 'Imposto sobre lucro', suffix: '%', hint: 'Taxa prudente sobre o resultado tributável. Em 2026 o IRC geral em Portugal é 19%; para PME os primeiros 50.000 € podem ser tributados a 15%. Ajustada para incluir derrama municipal.' },
  { key: 'cost_growth_percent', label: 'Crescimento de custos diretos', suffix: '%/ano', hint: 'Aumento anual do custo unitário de cada modelo (fornecedores + transporte).' },
  { key: 'fixed_costs_growth_percent', label: 'Crescimento de custos fixos', suffix: '%/ano', hint: 'Aumento anual das rubricas fixas (marketing, administração, eventos, operações).' },
  { key: 'variable_provision_growth_percent', label: 'Crescimento provisão variável', suffix: '%/ano', hint: 'Aumento anual da provisão variável por unidade vendida (garantias, assistência).' },
  { key: 'variable_provision_per_unit_eur', label: 'Provisão variável por unidade', prefix: '€', hint: 'Reserva por cada equipamento vendido para cobrir garantia, assistência técnica e devoluções.' },
  { key: 'depreciation_eur', label: 'Amortizações anuais', prefix: '€', hint: 'Amortização estimada sobre ativos incluídos no plano. Deduzida antes do imposto.' },
  { key: 'cost_buffer_percent', label: 'Buffer sobre custo direto', suffix: '%', hint: 'Margem de segurança adicionada ao custo direto de cada unidade para absorver variações cambiais e logísticas.' },
];

function euroLike(value, opts = {}) {
  const { prefix = '', suffix = '' } = opts;
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (prefix === '€') return `${n.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} €`;
  return `${n.toLocaleString('pt-PT', { maximumFractionDigits: 1 })}${suffix ? ' ' + suffix : ''}`;
}

export function AssumptionsPanel({ config, onEdit }) {
  if (!config) return null;
  return (
    <section id="pressupostos" className="acq-section" data-testid="assumptions-panel">
      <div className="acq-container">
        <span className="kicker">Pressupostos</span>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(1.6rem, 2.5vw, 2.2rem)', margin: '.5rem 0 .35rem' }}>
          Todos os números partem destes pressupostos.
        </h2>
        <p style={{ color: 'var(--muted)', maxWidth: '68ch', margin: '0 0 1.5rem' }}>
          Cada cartão explica exatamente o que está por trás dos cenários. Todos os valores podem ser editados no botão
          <strong style={{ margin: '0 .25em' }}>“Editar plano”</strong>
          no topo — as alterações recalculam imediatamente o hero, os cenários, a sensibilidade e o gráfico de payback.
        </p>

        <div className="assumptions-grid" data-testid="assumptions-grid">
          {CARDS.map((c) => (
            <article key={c.key} className="assumption-card" data-testid={`assumption-${c.key}`}>
              <header>
                <span className="assumption-label">{c.label}</span>
                <span className="assumption-help" title={c.hint} aria-label={c.hint}>
                  <HelpCircle size={14} />
                </span>
              </header>
              <strong className="assumption-value">{euroLike(config[c.key], { prefix: c.prefix, suffix: c.suffix })}</strong>
              <p className="assumption-hint">{c.hint}</p>
            </article>
          ))}
        </div>

        <div className="assumptions-secondary" data-testid="assumptions-secondary">
          <h3 style={{ marginTop: '2rem', marginBottom: '.5rem', fontSize: '1.1rem' }}>Custos fixos anuais no ano 1</h3>
          <div className="assumptions-mini-grid">
            <div><small>Total</small><strong>{euroLike(config.fixed_costs_year_one_eur, { prefix: '€' })}</strong></div>
            {config.fixed_costs_breakdown_eur && Object.entries(config.fixed_costs_breakdown_eur).map(([k, v]) => (
              <div key={k} data-testid={`fc-${k}`}>
                <small>{k === 'marketing' ? 'Marketing' : k === 'administration' ? 'Administração' : k === 'operations_service' ? 'Operações / Assistência' : k === 'events' ? 'Eventos' : k}</small>
                <strong>{euroLike(v, { prefix: '€' })}</strong>
              </div>
            ))}
          </div>
        </div>

        <div className="assumptions-cta" data-testid="assumptions-edit-cta">
          <Info size={16} />
          <div>
            <strong>Como editar estes pressupostos?</strong>
            <p>Carregue no botão <em>Editar plano</em> no cabeçalho. Terá secções separadas para Investimento inicial, Fiscalidade, Custos, Produtos (LioPT-5 e LioPT-9) e Cenários (unidades por ano, crescimento de preço). Ao guardar, todos os cartões e tabelas são recalculados.</p>
          </div>
          {onEdit && (
            <button className="btn btn-primary btn-sm" onClick={onEdit} data-testid="assumptions-edit-btn">
              Editar plano
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
